import { getAuth, signInWithCustomToken, signOut, User as FirebaseUser } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';

import { functions } from '../../firebase';
import { getFirebaseTokenRequest } from './firebase-functions';

export interface FirebaseIdentity {
  userId: string;
  email?: string;
  phone?: string;
}

// Single-flight: concurrent callers (chat list + notifications + forms all
// mounting at once) must share ONE custom-token exchange, not race N of them.
let pendingSignIn: Promise<FirebaseUser | null> | null = null;

const signInToFirebase = async (identity: FirebaseIdentity): Promise<FirebaseUser | null> => {
  try {
    const res = await getFirebaseTokenRequest(identity);
    const cred = await signInWithCustomToken(getAuth(), res.data.firebaseToken);
    if (cred.user.uid !== identity.userId) {
      // A session under the wrong uid is worse than none: once the backend
      // enforces auth it would act as another user. Drop it and report.
      console.error(`Firebase uid mismatch: expected ${identity.userId}, got ${cred.user.uid}`);
      await signOut(getAuth()).catch(() => undefined);
      return null;
    }
    return cred.user;
  } catch (e: any) {
    // Log the server error body/message only — never the axios error object,
    // whose `config.headers.Authorization` carries a live Bearer token.
    console.error('Firebase sign-in failed', e?.response?.data ?? e?.message);
    return null;
  }
};

/**
 * Guarantees a Firebase Auth session whose uid matches the logged-in user.
 * Reuses the persisted session when there is one (Firebase restores it across
 * page loads), signs in with a custom token otherwise. Returns null on failure
 * so callers degrade exactly like the old ad-hoc flow.
 */
export const ensureFirebaseSession = async (identity: FirebaseIdentity): Promise<FirebaseUser | null> => {
  if (!identity.userId) return null;
  const auth = getAuth();
  // currentUser is null until Firebase finishes restoring persistence — wait,
  // or every page load would needlessly mint a new custom token.
  await auth.authStateReady();
  if (auth.currentUser?.uid === identity.userId) {
    return auth.currentUser;
  }
  if (pendingSignIn) {
    const user = await pendingSignIn;
    if (user?.uid === identity.userId) return user;
  }
  if (auth.currentUser && auth.currentUser.uid !== identity.userId) {
    // Leftover session from a previously logged-in account.
    await signOut(auth).catch(() => undefined);
  }
  pendingSignIn = signInToFirebase(identity).finally(() => {
    pendingSignIn = null;
  });
  return pendingSignIn;
};

/**
 * Fresh Firebase ID token for the given user. getIdToken() serves the cached
 * token and transparently refreshes it near expiry — no re-login needed.
 */
export const getFirebaseIdToken = async (identity: FirebaseIdentity): Promise<string | null> => {
  const user = await ensureFirebaseSession(identity);
  if (!user) return null;
  try {
    return await user.getIdToken();
  } catch (e: any) {
    console.error('Failed to get Firebase ID token', e?.message);
    return null;
  }
};

/** Call on app logout so the Firebase session never outlives the app session. */
export const signOutOfFirebase = async () => {
  try {
    await signOut(getAuth());
  } catch (e: any) {
    console.error('Firebase sign-out failed', e?.message);
  }
};

// The callable client attaches the current user's ID token by itself; the only
// gap is a call racing session restore / the initial sign-in. Wait for those to
// settle, then proceed regardless: until the backend enforces auth an
// unauthenticated call must keep working, and after that the shadow logs will
// have confirmed this path is dry.
const waitForFirebaseAuthSettled = async () => {
  try {
    await getAuth().authStateReady();
    if (pendingSignIn) await pendingSignIn;
  } catch {
    // Never block a request on auth plumbing.
  }
};

/**
 * Drop-in replacement for `httpsCallable(functions, name)` that waits for the
 * Firebase session to settle before invoking, so every callable carries the
 * user's ID token once the global sign-in has happened.
 */
export const callable = (name: string) => {
  const fn = httpsCallable(functions, name);
  return async (payload?: unknown) => {
    await waitForFirebaseAuthSettled();
    return fn(payload);
  };
};
